import { store } from 'store/store';

export enum DEPEND_CONDITION_RULES {
	NOT_APPLICABLE = 'NotApplicable',
	MATCH_REGULAR_EXPRESSION = 'MatchRegularExpression',
	EMPTY = 'Empty',
	EQUALS = 'Equals',
	NOT_EQUALS = 'NotEquals',
	CONTAINS = 'Contains',
	NOT_CONTAINS = 'NotContains',
	GREATER_THAN = 'GreaterThan',
	LESS_THAN = 'LessThan',
	GREATER_THAN_OR_EQUAL_TO = 'GreaterThanOrEqualTo',
	LESS_THAN_OR_EQUAL_TO = 'LessThanOrEqualTo',
}

// Keep the first Empty-rule result so prefilled-step behavior stays stable during navigation.
// TODO(CD-01): Decide when this cache should reset; see docs/content-dependencies.md.
const fieldIsEmptyResolved: Map<string, boolean> = new Map();

export const isFieldHidden = (field: FieldsContent): boolean => {
	if (field.content.contentType === 'ModalContentBlock' || field.content.contentType === 'FlowRestrictionBlock') {
		return false;
	}
	return isHidden(field?.content?.contentDependency);
};
export const isPageHidden = (page: StepBlock): boolean => {
	const isHidden_ = isHidden(page?.contentDependency);
	if (!isHidden_) {
		const visibleFields = page?.fields?.items?.some(
			(field) =>
				!isFieldHidden(field) &&
				field.content.contentType !== 'ModalContentBlock' &&
				field.content.contentType !== 'FlowRestrictionBlock'
		);
		return !visibleFields;
	}
	return isHidden_;
};

// CMS can send a group without conditions, so identify groups by their keys rather than their child count.
const isGroup = (dependency: ContentDependency | null | undefined): dependency is ContentDependencyGroup =>
	dependency != null && ('logic' in dependency || 'conditions' in dependency);

/** Evaluate nested groups and let each caller decide how to compare individual rules. */
export const evaluateDependency = (
	dependency: ContentDependency | null | undefined,
	evaluateLeaf: (leaf: ContentDependencyLeaf) => boolean
): boolean => {
	if (!isGroup(dependency)) {
		return dependency == null ? true : evaluateLeaf(dependency as ContentDependencyLeaf);
	}

	// A group with no conditions matches when it is AND and does not match when it is OR.
	// TODO(CD-03): Confirm with product how empty groups and unknown logic should behave.
	const children = Array.isArray(dependency.conditions) ? dependency.conditions : [];
	switch (dependency.logic) {
		case 'AND':
			return children.every((condition) => evaluateDependency(condition, evaluateLeaf));
		case 'OR':
			return children.some((condition) => evaluateDependency(condition, evaluateLeaf));
		default:
			// Missing or unknown logic means the rule does not restrict visibility.
			return true;
	}
};

export const getSuppliedAnswer = (fieldId: string, suppliedValue: unknown): unknown => {
	if (suppliedValue !== null && typeof suppliedValue === 'object') {
		return (suppliedValue as Record<string, unknown>)[fieldId];
	}
	return suppliedValue;
};

/** Return true when the dependency matches and its field or page can be shown. */
export const meetDependency = (dependency: ContentDependency | null | undefined, value?) => {
	return evaluateDependency(dependency, (leaf) => {
		const fieldId = leaf.dependsOn?.uid;
		if (!fieldId) {
			return !isLeafHidden(leaf, undefined);
		}
		const suppliedAnswer = getSuppliedAnswer(fieldId, value);
		const answer = suppliedAnswer ?? store.getState().calculatorForm[fieldId]?.data?.value;
		return !isLeafHidden(leaf, answer);
	});
};

/** List each field referenced by a rule once, including rules inside nested groups. */
export const getDependencyFieldIds = (dependency?: ContentDependency | null): string[] => {
	const fieldIds: string[] = [];
	const pending = dependency ? [dependency] : [];
	while (pending.length) {
		const current = pending.pop();
		if (isGroup(current)) {
			if (Array.isArray(current.conditions)) {
				pending.unshift(...current.conditions);
			}
			continue;
		}
		const leaf = current as ContentDependencyLeaf | undefined;
		const id = leaf?.dependsOn?.uid;
		if (id && !fieldIds.includes(id)) {
			fieldIds.push(id);
		}
	}
	return fieldIds;
};

/** Find whether any rule uses this condition, regardless of whether it currently matches. */
export const hasDependencyCondition = (
	dependency: ContentDependency | null | undefined,
	condition: dependConditionRules
): boolean => {
	if (isGroup(dependency)) {
		return Array.isArray(dependency.conditions)
			? dependency.conditions.some((child) => hasDependencyCondition(child, condition))
			: false;
	}
	return (dependency as ContentDependencyLeaf | null | undefined)?.dependCondition === condition;
};

const isHidden = (dependency: ContentDependency | null | undefined, value?): boolean => {
	return !meetDependency(dependency, value);
};

const isLeafHidden = (leaf: ContentDependencyLeaf, fieldValue): boolean => {
	const dependsOnId = leaf?.dependsOn?.uid;
	if (dependsOnId) {
		switch (leaf.dependCondition) {
			case DEPEND_CONDITION_RULES.NOT_APPLICABLE:
				return existDependency(fieldValue);
			case DEPEND_CONDITION_RULES.EQUALS:
				return equals(leaf, fieldValue);
			case DEPEND_CONDITION_RULES.EMPTY:
				return empty(leaf, fieldValue);
			case DEPEND_CONDITION_RULES.NOT_EQUALS:
				return !equals(leaf, fieldValue);
			// TODO(CD-05): Add these operators after agreeing how visibility and submit validation compare values.
			case DEPEND_CONDITION_RULES.MATCH_REGULAR_EXPRESSION:
				return false;
			case DEPEND_CONDITION_RULES.CONTAINS:
				return contains(leaf, fieldValue);
			case DEPEND_CONDITION_RULES.NOT_CONTAINS:
				return !contains(leaf, fieldValue);
			case DEPEND_CONDITION_RULES.GREATER_THAN:
				return greaterThan(leaf, fieldValue);
			case DEPEND_CONDITION_RULES.LESS_THAN:
				return lessThan(leaf, fieldValue);
			default:
				return false;
		}
	}
	return false;
};

const existDependency = (dependentValue): boolean => {
	return dependentValue === undefined || dependentValue === null;
};

const equals = (dependency: ContentDependencyLeaf, dependentValue): boolean => {
	return dependency.dependValue !== dependentValue;
};

const empty = (dependency: ContentDependencyLeaf, dependentValue): boolean => {
	if (fieldIsEmptyResolved.get(dependency.dependsOn.uid) !== undefined) {
		return fieldIsEmptyResolved.get(dependency.dependsOn.uid);
	}

	const trimmedValue = dependentValue?.toString()?.trim();

	switch (trimmedValue) {
		case undefined:
		case null:
		case '':
			fieldIsEmptyResolved.set(dependency.dependsOn.uid, false);
			return false;
		default:
			fieldIsEmptyResolved.set(dependency.dependsOn.uid, true);
			return true;
	}
};

const greaterThan = (dependency: ContentDependencyLeaf, dependentValue): boolean => {
	if (!dependency.dependValue) return false;
	const { value, threshold } = checkForDateScript(dependency.dependValue);
	if (threshold !== undefined) {
		return value - dependentValue < threshold;
	}
	return dependentValue < value;
};

const lessThan = (dependency: ContentDependencyLeaf, dependentValue): boolean => {
	if (!dependency.dependValue) return false;
	return dependentValue >= dependency.dependValue;
};

const contains = (dependency: ContentDependencyLeaf, dependentValue): boolean => {
	if (!dependency.dependValue) return false;
	return !dependentValue?.includes(dependency.dependValue);
};

// Resolve supported date expressions; parse other expressions as numbers.
const checkForDateScript = (dependValue: string): { value: number; threshold?: number } => {
	const knownExpressions: Record<string, () => number> = {
		'new Date().getFullYear()': () => new Date().getFullYear(),
		'new Date().getMonth()': () => new Date().getMonth(),
		'new Date().getDate()': () => new Date().getDate(),
	};

	const [expressionPart, thresholdPart] = dependValue.split(',').map((s) => s.trim());
	const resolver = knownExpressions[expressionPart];
	const value = resolver ? resolver() : Number(expressionPart);
	const threshold = thresholdPart === undefined ? undefined : Number(thresholdPart);

	return { value, threshold };
};
